import React from 'react';
import { Box, Typography, Button, Link as MuiLink, Container, Grid } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import './Footer.css'; // optional if you're keeping some custom styles

const labels = {
  si: {
    heading: 'හොඳම විවාහ සැලසුම් සඳහා සම්බන්ධ වන්න',
    signUp: 'දැන්ම ලියාපදිංචි වන්න',
    socialMedia: 'සමාජ ජාල',
    instagram: 'ඉන්ස්ටග්‍රෑම්',
    facebook: 'ෆේස්බුක්',
    youtube: 'යූටියුබ්',
    contactUs: 'අපව සම්බන්ධ වන්න',
    aboutUs: 'අප සම්බන්ධව',
    termsCondition: 'නියමයන් සහ කොන්දේසි',
    copyright: 'යුවතිපතී',
  },
  en: {
    heading: 'Connect for the Best Wedding Plans',
    signUp: 'Sign Up Now',
    socialMedia: 'Social Media',
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    contactUs: 'Contact Us',
    aboutUs: 'About Us',
    termsCondition: 'Terms & Conditions',
    copyright: 'Yuwathipathi',
  }
};

function Footer({ language }) {
  const navigate = useNavigate();
  const theme = useTheme();

  // Clean, prop-driven language selection
  const t = labels[language] ?? labels.si;

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        backgroundColor: isDark ? theme.palette.background.default : theme.palette.primary.main,
        color: isDark ? theme.palette.text.primary : theme.palette.primary.contrastText,
        borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: isDark ? 'text.primary' : 'primary.contrastText',
              fontWeight: 700,
            }}
          >
            {t.heading}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/profile')}
            sx={{
              mt: 2,
              borderRadius: '3rem',
              padding: '12px 24px',
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#e9e9e9',
              fontSize: '0.85rem',
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#ccc',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              },
            }}
          >
            {t.signUp}
          </Button>

          <Grid container spacing={20} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: isDark ? 'text.primary' : 'primary.contrastText',
                  fontWeight: 600,
                }}
              >
                {t.socialMedia}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <MuiLink
                  component={Link}
                  to="/"
                  color="inherit"
                  underline="hover"
                  sx={{
                    color: isDark ? 'text.secondary' : 'primary.contrastText',
                    opacity: 0.9,
                    '&:hover': {
                      opacity: 1,
                      color: 'primary.contrastText',
                    },
                  }}
                >
                  {t.instagram}
                </MuiLink>
                <MuiLink
                  href="https://www.facebook.com/share/16AGRMhGVQ/"
                  target="_blank"
                  color="inherit"
                  underline="hover"
                  sx={{
                    color: isDark ? 'text.secondary' : 'primary.contrastText',
                    opacity: 0.9,
                    '&:hover': {
                      opacity: 1,
                      color: 'primary.contrastText',
                    },
                  }}
                >
                  {t.facebook}
                </MuiLink>
                <MuiLink
                  href="https://www.youtube.com/watch?v=PZqhJSdBoy0"
                  target="_blank"
                  color="inherit"
                  underline="hover"
                  sx={{
                    color: 'primary.contrastText',
                    opacity: 0.9,
                    '&:hover': {
                      opacity: 1,
                      color: 'primary.contrastText',
                    },
                  }}
                >
                  {t.youtube}
                </MuiLink>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={5}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: isDark ? 'text.primary' : 'primary.contrastText',
                  fontWeight: 600,
                }}
              >
                {t.contactUs}
              </Typography>
              <MuiLink
                component={Link}
                to="/Infor-page"
                color="inherit"
                underline="hover"
                sx={{
                  color: isDark ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.9,
                  '&:hover': {
                    opacity: 1,
                    color: 'primary.contrastText',
                  },
                }}
              >
                {t.aboutUs}
              </MuiLink>
              <br />
              <MuiLink
                component={Link}
                to="/terms-and-conditions"
                color="inherit"
                underline="hover"
                sx={{
                  color: isDark ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.9,
                  '&:hover': {
                    opacity: 1,
                    color: 'primary.contrastText',
                  },
                }}
              >
                {t.termsCondition}
              </MuiLink>
            </Grid>
          </Grid>

          <Box mt={6} textAlign="center">
            <Typography
              onClick={() => navigate('/login-admin')}
              variant="body2"
              sx={{
                color: isDark ? 'text.secondary' : 'primary.contrastText',
                opacity: 0.9,
              }}
            >
              {t.copyright} © 2024
            </Typography>
          </Box>

          <Box mt={3} textAlign="center">
            <Box display="flex" justifyContent="center" gap={2}>
              <MuiLink
                href="/"
                target="_blank"
                aria-label="Facebook"
                color="inherit"
                sx={{
                  color: isDark ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.8,
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(-2px)',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <i className="fab fa-facebook-f" />
              </MuiLink>
              <MuiLink
                href="/"
                target="_blank"
                aria-label="Instagram"
                color="inherit"
                sx={{
                  color: isDark ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.8,
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(-2px)',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <i className="fab fa-instagram" />
              </MuiLink>
              <MuiLink
                href="/"
                target="_blank"
                aria-label="YouTube"
                color="inherit"
                sx={{
                  color: isDark ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.8,
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(-2px)',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <i className="fab fa-youtube" />
              </MuiLink>
              <MuiLink
                href="/"
                target="_blank"
                aria-label="Twitter"
                color="inherit"
                sx={{
                  color: isDark ? 'text.secondary' : 'primary.contrastText',
                  opacity: 0.8,
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(-2px)',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <i className="fab fa-twitter" />
              </MuiLink>
              <MuiLink
                href="/"
                target="_blank"
                aria-label="LinkedIn"
                color="inherit"
                sx={{
                  color: 'primary.contrastText',
                  opacity: 0.8,
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(-2px)',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <i className="fab fa-linkedin" />
              </MuiLink>
            </Box>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default Footer;
